import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import styled, { css } from 'styled-components';
import LoadingIndicator from 'src/components/LoadingIndicator';
import { useSelector, useDispatch } from 'react-redux';

import moment from 'moment';
import { capitalize } from 'src/utils/string.utils';
import { Chart } from '@snapper/charts';
import { Switch } from 'src/components/Switch';
import { getBrandingColor } from 'src/branding';
import { usePageSize, usePageWidth } from 'src/utils/hooks';
import { Breadcrumbs } from 'src/components/Breadcrumbs';
import {
  useMountEffect,
  repeatNTimesIntoFlatArray,
  unique,
  formatAmount,
  formatPercentage,
  isThisLastYearValidForPercentageDiff,
  timePeriodKindSubkindsForAllFarmerStats,
  getValidTimePeriods,
  getValidSubkindsForTimePeriodAndKind,
  getValidKindsForTimePeriod,
  kindToDisplay,
  allTimePeriods,
} from './utils.js';
import DiseaseStatisticsTable, {
  DiseaseNameHeader,
} from '../../components/DiseaseStatisticsTable';
import {
  getFarmerStatistics,
  getAnimalKindsWithStats,
  getSlaughterhouseStatistics,
} from '../../store/accountings/accountings.selectors';
import { accountingsActions } from '../../store/accountings/accountings.actions';
import Helptext from '../../components/Helptext';
import {
  Row, Label,
} from '../../components/Layout';
import {
  GeneralStatsEntity,
  FilterRow,
  ActionButton,
  StatisticsPage,
  ExpandGeneralStats,
  Header,
  FilterDropdown,
  SwtichWrapper,
} from './Components';

const Statistics = () => {
  const dispatch = useDispatch();
  const farmerStatistics = useSelector(getFarmerStatistics);
  const slaughterhouseStatistics = useSelector(getSlaughterhouseStatistics); // agregated statistics for entire slaughterhouse
  const animalKindsWithFarmerStats = useSelector(getAnimalKindsWithStats);
  const subkindKeeping = animalKindsWithFarmerStats?.data?.kinds_with_stats;

  const [selectedAnimalKind, setSelectedAnimalKind] = useState(null); // eg pigs, sheep
  const [selectedAnimalSubkind, setSelectedAnimalSubkind] = useState(null); // eg goat
  const [selectedTimePeriod, setSeletedTimePeriod] = useState(null); // eg this year, last year
  const [selectedBottomView, setSelectedBottomView] = useState('graph');

  const [generalStatsExpanded, setGeneralStatsExpanded] = useState(false);

  const referenceWidthElem = useRef();
  const pageSize = usePageSize();



  /* When one of the filtering parameters has changed, get the new statistics, if we dont already have it.
  When we have got the data, we may need to change the selected fitlering so that it is valid for the current data(eg if we switched from pigs to sheep,
    a sheep group can not be selected after the switch
    )
  */
  const fetchDataBasedOnFilters = (timePeriod, animalKind, animalSubkind) => {
    if (farmerStatistics.error || slaughterhouseStatistics.error || animalKindsWithFarmerStats.error) {
      return;
    }

    if (!timePeriod || !animalKind || !animalSubkind) {
      return;
    }

    if (timePeriod.value === 'compare-this-last-year') {
      if (
        !farmerStatistics.data['this-year']?.[animalKind.value]
          ?.subkinds?.[animalSubkind.value]
      ) {
        dispatch(
          accountingsActions.fetchFarmerStatistics({
            timeFrame: 'this-year',
            subkind: animalSubkind.value,
            kind: animalKind.value,
          }),
        );
      }
      if (
        !farmerStatistics.data['last-year']?.[animalKind.value]
          ?.subkinds?.[animalSubkind.value]
      ) {
        dispatch(
          accountingsActions.fetchFarmerStatistics({
            timeFrame: 'last-year',
            subkind: animalSubkind.value,
            kind: animalKind.value,
          }),
        );
      }
      return;
    }

    if (
      (!farmerStatistics.data && !farmerStatistics.isFetching)
      || (farmerStatistics.data
        && !farmerStatistics.data[timePeriod.value]?.[
          animalKind.value
        ]?.subkinds?.[animalSubkind.value])
    ) {
      dispatch(
        accountingsActions.fetchFarmerStatistics({
          timeFrame: timePeriod.value,
          subkind: animalSubkind.value,
          kind: animalKind.value,
        }),
      );
    }
  };

  /* When filtering criterion is changed, we may also need to update the other filterings so that only valid filtering combinations are set.
  Setting default filters is also used when initially setting up the filterings
  */
  const setDefaultSelectedFilters = useCallback(
    (data, toSet = ['TIME_FRAME', 'KIND', 'SUBKIND'], usingTimeframe) => {
      setGeneralStatsExpanded(false);
      const changeTimePeriod = toSet.indexOf('TIME_FRAME') >= 0;
      const changeKind = toSet.indexOf('KIND') >= 0;
      const changeSubkind = toSet.indexOf('SUBKIND') >= 0;

      let firstTimePeriodWithSlaughters = changeTimePeriod
        ? allTimePeriods.find(
          (e) => data.kinds_with_stats[e.value].has_slaughters,
        )
        : selectedTimePeriod;
      if (usingTimeframe) {
        firstTimePeriodWithSlaughters = usingTimeframe;
      }

      let firstValidKindForPeriod = changeKind ? null : selectedAnimalKind;

      if (changeKind) {
        let subkindsForTimePeriod = [];
        if (firstTimePeriodWithSlaughters.value === 'compare-this-last-year') {
          subkindsForTimePeriod = unique(
            Object.getOwnPropertyNames(
              data.kinds_with_stats['this-year'],
            ).concat(
              Object.getOwnPropertyNames(data.kinds_with_stats['this-year']),
            ),
          )
            .filter((sk) => sk !== 'has_slaughters')
            .sort();
        } else {
          subkindsForTimePeriod = Object.getOwnPropertyNames(
            data.kinds_with_stats[firstTimePeriodWithSlaughters.value],
          )
            .filter((sk) => sk !== 'has_slaughters')
            .sort();
        }

        const hasSlaughtersForKind = (kind) => {
          if (
            firstTimePeriodWithSlaughters.value === 'compare-this-last-year'
          ) {
            return (
              data.kinds_with_stats['this-year'][kind].has_slaughters
              && data.kinds_with_stats['last-year'][kind].has_slaughters
            );
          } else {
            return data.kinds_with_stats[firstTimePeriodWithSlaughters.value][
              kind
            ].has_slaughters;
          }
        };
        for (const kindForPeriod of subkindsForTimePeriod) {
          if (hasSlaughtersForKind(kindForPeriod)) {
            firstValidKindForPeriod = {
              text: kindForPeriod,
              value: kindForPeriod,
            };
            break;
          }
        }
      }

      const firstValidSubkindForKind = changeSubkind
        ? { text: 'ALL_SUBKINDS', value: 'ALL_SUBKINDS' }
        : selectedAnimalSubkind;
      if (
        changeTimePeriod
        && selectedTimePeriod?.value !== firstTimePeriodWithSlaughters.value
      ) {
        setSeletedTimePeriod(firstTimePeriodWithSlaughters);
      }
      if (
        changeKind
        && selectedAnimalKind?.value !== firstValidKindForPeriod.value
      ) {
        setSelectedAnimalKind(firstValidKindForPeriod);
      }
      if (
        changeSubkind
        && selectedAnimalSubkind?.value !== firstValidSubkindForKind.value
      ) {
        setSelectedAnimalSubkind(firstValidSubkindForKind);
      }
    },
    [selectedAnimalKind, selectedAnimalSubkind, selectedTimePeriod],
  );

  /* Get slaughterhouse statistics. These are pre-generated on the server, so we can get all of the statistics right away */
  useEffect(() => {
    if (
      !slaughterhouseStatistics.data
      && !slaughterhouseStatistics.isFetching
      && !slaughterhouseStatistics.error
    ) {
      dispatch(accountingsActions.fetchSlaughterhouseStatistics());
    }
  }, [
    dispatch,
    slaughterhouseStatistics.data,
    slaughterhouseStatistics.error,
    slaughterhouseStatistics.isFetching,
  ]);

  /* Load data about which time periods, kinds and subkinds the user has stats for, and select the first valid of each or skip right to the selection
  if we already has the data
   */
  useEffect(() => {
    if (
      !animalKindsWithFarmerStats.data
      && !animalKindsWithFarmerStats.isFetching
      && !animalKindsWithFarmerStats.error
    ) {
      dispatch(
        accountingsActions.fetchAnimalKindsWithStats({
          onSuccess: (data) => {
            if (!missingSlaughters) {
              setDefaultSelectedFilters(data, ['TIME_FRAME', 'KIND', 'SUBKIND']);
            }
          },
        }),
      );
    }

    if (
      animalKindsWithFarmerStats.data
      && !farmerStatistics.data
      && !farmerStatistics.isFetching
    ) {
      if (!missingSlaughters) {
        setDefaultSelectedFilters(animalKindsWithFarmerStats.data, [
          'TIME_FRAME',
          'KIND',
          'SUBKIND',
        ]);
      }

      if (!farmerStatistics.isFetchingAll && subkindKeeping) {
        fetchAllFarmerStatistics();
      }
    }
  }, [
    animalKindsWithFarmerStats.data,
    animalKindsWithFarmerStats.isFetching,
    dispatch,
    farmerStatistics,
    farmerStatistics.data,
    farmerStatistics.isFetching,
    setDefaultSelectedFilters,
  ]);

  // we have visited this page before and the data is still in the store
  useMountEffect(() => {
    if (farmerStatistics.data && animalKindsWithFarmerStats.data && !missingSlaughters) {
      setDefaultSelectedFilters(animalKindsWithFarmerStats.data, [
        'TIME_FRAME',
        'KIND',
        'SUBKIND',
      ]);
    }
  });

  /* Loops trough all time periods, all kinds and subkinds and fetches the relevant farmer statistic */
  const fetchAllFarmerStatistics = () => {
    const toFetch = timePeriodKindSubkindsForAllFarmerStats(kindToDisplay, subkindKeeping, allTimePeriods);

    dispatch(accountingsActions.fetchAllFarmerStatistics({
      timeFrame: Array.from(toFetch.allTimePeriods).join(';'),
      kind: Array.from(toFetch.allKinds).join(';'),
      subkind: Array.from(toFetch.allSubkinds).join(';'),
    }));
  };

  /* Check and maybe fetch new data when filters are changed */
  useEffect(() => {
    if (
      selectedAnimalKind?.value
      && selectedTimePeriod?.value
      && selectedAnimalSubkind?.value
    ) {
      fetchDataBasedOnFilters(selectedTimePeriod, selectedAnimalKind, selectedAnimalSubkind);
    }
  }, [
    fetchDataBasedOnFilters,
    selectedAnimalKind,
    selectedAnimalSubkind,
    selectedTimePeriod,
  ]);

  const hasErrorLoadingMain = animalKindsWithFarmerStats.error || slaughterhouseStatistics.error;

  const missingDataOrSetupBase = !slaughterhouseStatistics.data
    || !farmerStatistics.data
    || farmerStatistics.isFetching
    || !animalKindsWithFarmerStats.data
    || animalKindsWithFarmerStats.isFetching
    || !selectedTimePeriod
    || !selectedAnimalKind
    || !selectedAnimalSubkind;

  const missingSlaughters = Object.getOwnPropertyNames(animalKindsWithFarmerStats.data?.kinds_with_stats || {}).every((time_frame) => (animalKindsWithFarmerStats.data?.kinds_with_stats?.[time_frame] || {})?.has_slaughters === false);

  const missingDataOrSetupCurrentView = () => {
    if (farmerStatistics.fetchedAll) {
      return false;
    }
    if (selectedTimePeriod.value === 'compare-this-last-year') {
      return (
        [true, null, undefined].indexOf(
          farmerStatistics.data?.['this-year']?.[selectedAnimalKind.value]
            ?.subkinds?.[selectedAnimalSubkind.value]?.isFetching,
        ) >= 0
        || [true, null, undefined].indexOf(
          farmerStatistics.data?.['last-year']?.[selectedAnimalKind.value]
            ?.subkinds?.[selectedAnimalSubkind.value]?.isFetching,
        ) >= 0
      );
    } else {
      const fetching = farmerStatistics.data?.[selectedTimePeriod.value]?.[
        selectedAnimalKind.value
      ]?.subkinds?.[selectedAnimalSubkind.value]?.isFetching;

      return fetching === undefined || fetching === null || fetching === true;
    }
  };
  const hasErrorLoadingDataContent = farmerStatistics.error;

  const getDataForTimePeriod = (timePeriod = selectedTimePeriod.value) => {
    const forFarmer = farmerStatistics.data[timePeriod][selectedAnimalKind.value].subkinds[
      selectedAnimalSubkind.value
    ];
    const farmerGeneralStats = { ...forFarmer };
    delete farmerGeneralStats.diseases;

    if (timePeriod === 'last-settlement') {
      return {
        generalStats: { farmer: farmerGeneralStats, slaughterhouse: null },
        diseases: forFarmer.diseases
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((ds) => ({
            slaughterhouse: null,
            farmer: ds,
          })),
        partially_discarded: { farmer: forFarmer.partially_discarded },
        total_discarded: { farmer: forFarmer.total_discarded },
      };
    }

    const forSlaughterhouse = timePeriod === 'last-settlement'
      ? null
      : slaughterhouseStatistics.data[timePeriod][selectedAnimalKind.value]
        .subkinds[selectedAnimalSubkind.value];
    const slaughterhouseGeneralStats = { ...forSlaughterhouse };
    delete slaughterhouseGeneralStats.diseases;

    // we need to add diseases both from slaughterhouse-data and from farmer-data
    // this is because diseases for slaughterhouse is cached, so there may be
    // diseases for farmer not yet present in diseases for slaughterhouse
    const diseasesUsingSlaughterhouse = forSlaughterhouse.diseases
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((ds) => ({
        slaughterhouse: ds,
        farmer: forFarmer.diseases.find((df) => df.name === ds.name) || null,
      }));

    const diseases = [...diseasesUsingSlaughterhouse];
    forFarmer.diseases.forEach((df) => {
      const diseasInSlaughterhouseDiseases = diseasesUsingSlaughterhouse.find((ds) => df.name === ds.slaughterhouse.name);
      if (diseasInSlaughterhouseDiseases) {
        return;
      }
      diseases.push({
        slaughterhouse: { name: df.name, percentage: 0, count: 0 },
        farmer: df,
      });
    });

    return {
      generalStats: {
        farmer: farmerGeneralStats,
        slaughterhouse: slaughterhouseGeneralStats,
      },
      diseases,
      partially_discarded: {
        farmer: forFarmer.partially_discarded,
        slaughterhouse: forSlaughterhouse.partially_discarded,
      },
      total_discarded: {
        farmer: forFarmer.total_discarded,
        slaughterhouse: forSlaughterhouse.total_discarded,
      },
    };
  };

  const getPresentationDataForDiscardedAnimals = (data, time_frame) => {
    const farmerPartially = [
      {
        value: formatPercentage(data.partially_discarded.farmer.percentage),
        customDisplayValue: percentageDisplay(data.partially_discarded.farmer.percentage),
      },
      { value: data.partially_discarded.farmer.count },
    ];
    const farmerTotal = [
      {
        value: formatPercentage(data.total_discarded.farmer.percentage),
        customDisplayValue: percentageDisplay(data.total_discarded.farmer.percentage),
      },
      { value: data.total_discarded.farmer.count },
    ];
    let slaughterhousePartially = null;
    let slaughterhouseTotal = null;
    if (time_frame !== 'last-settlement') {
      slaughterhousePartially = [
        {
          value: formatPercentage(data.partially_discarded.slaughterhouse.percentage),
          customDisplayValue: percentageDisplay(data.partially_discarded.slaughterhouse.percentage),
        },
        { value: data.partially_discarded.slaughterhouse.count },
      ];
      slaughterhouseTotal = [
        {
          value: formatPercentage(data.total_discarded.slaughterhouse.percentage),
          customDisplayValue: percentageDisplay(data.total_discarded.slaughterhouse.percentage),
        },
        { value: data.total_discarded.slaughterhouse.count },
      ];
    }

    return {
      partially: {
        farmer: farmerPartially,
        slaughterhouse: slaughterhousePartially,
      },
      total: { farmer: farmerTotal, slaughterhouse: slaughterhouseTotal },
    };
  };

  const percentageDisplay = (percentage, options) => {
    // values smaller thatn 0.01 may be rounded away, so display it so that the user knows and
    // dosent think the value is actually 0
    if (percentage !== 0 && percentage > 0 && percentage < 0.01) {
      return '< 0.01';
    }

    const shouldShowIncrement = percentage > 0 && options && options.showIncrement;
    const shouldShowPercentageSign = options && options.showPercentageSign;

    const shouldFormatPercentage = options && options.formatPercentage;
    if (!shouldShowIncrement && !shouldFormatPercentage) {
      return null;
    }

    const perc = shouldShowIncrement ? `+${(shouldFormatPercentage && formatPercentage(percentage)) || percentage}`
      : ((shouldFormatPercentage && formatPercentage(percentage)) || percentage);

    if (shouldShowPercentageSign && perc !== 'N/A') {
      return `${perc}%`;
    }
    if (perc === 'N/A') {
      return '';
    }
    return perc;
  };

  const countDisplay = (count) => {
    if (count > 0) {
      return `+${count}`;
    } else {
      return count;
    }
  };

  const getTableInformationByTimePeriod = (timePeriod) => {
    let dataForCurrFilters = null;
    let discardedCurrFilters = null;
    let totalDiscardFarmerAndSlaughterhouse = null;
    let partiallyDiscardFarmerAndSlaughterhouse = null;
    if (timePeriod !== 'compare-this-last-year') {
      dataForCurrFilters = getDataForTimePeriod(timePeriod);
      discardedCurrFilters = getPresentationDataForDiscardedAnimals(
        dataForCurrFilters,
        timePeriod,
      );

      totalDiscardFarmerAndSlaughterhouse = dataForCurrFilters.diseases.length > 0
        ? {
          diseaseData: [
            { value: 'Helkassert' },
            ...discardedCurrFilters.total.farmer.concat(
              ...(discardedCurrFilters.total.slaughterhouse || []),
            ),
          ],
        }
        : [];
      partiallyDiscardFarmerAndSlaughterhouse = dataForCurrFilters.diseases.length > 0
        ? {
          diseaseData: [
            {
              value: 'Delkassert',
            },
            ...discardedCurrFilters.partially.farmer.concat(
              ...(discardedCurrFilters.partially.slaughterhouse || []),
            ),
          ],
        }
        : [];
    }
    if (timePeriod === 'last-settlement') {
      return {
        headers: [
          { value: 'Syke siste innmelding', itemProps: { colSpan: 2 } },
        ],
        subHeaders: repeatNTimesIntoFlatArray(1, [
          { value: '%' },
          { value: 'Antall' },
        ]),
        data: dataForCurrFilters.diseases
          .map((disease) => ({
            diseaseData: [
              { value: disease.farmer.name },
              {
                value: formatPercentage(disease.farmer.percentage),
                customDisplayValue: percentageDisplay(disease.farmer.percentage),
              },
              { value: disease.farmer.count || 0 },
            ],
          }))
          .concat(
            dataForCurrFilters.diseases.length > 1
              ? {
                diseaseData: [
                  { value: 'Helkassert' },
                  ...discardedCurrFilters.total.farmer,
                ],
              }
              : [],
          )
          .concat(
            dataForCurrFilters.diseases.length > 1
              ? {
                diseaseData: [
                  { value: 'Delkassert' },
                  ...discardedCurrFilters.partially.farmer,
                ],
              }
              : [],
          ),
      };
    }

    if (timePeriod === 'last-year') {
      return {
        headers: [
          { value: 'Syke i fjor', itemProps: { colSpan: 2 } },
          { value: 'Slakteri i fjor', itemProps: { colSpan: 2 } },
        ],
        subHeaders: repeatNTimesIntoFlatArray(2, [
          { value: '%' },
          { value: 'Antall' },
        ]),
        data: dataForCurrFilters.diseases
          .map((disease) => ({
            diseaseData: [
              { value: disease.slaughterhouse?.name },
              {
                value: formatPercentage(disease.farmer?.percentage),
                customDisplayValue: percentageDisplay(disease.farmer?.percentage),
              },
              { value: disease.farmer?.count || 0 },
              {
                value: formatPercentage(disease.slaughterhouse?.percentage),
                customDisplayValue: percentageDisplay(disease.slaughterhouse?.percentage),
              },
              { value: disease.slaughterhouse?.count },
            ],
          }))
          .concat(totalDiscardFarmerAndSlaughterhouse)
          .concat(partiallyDiscardFarmerAndSlaughterhouse),
      };
    }
    if (timePeriod === 'compare-this-last-year') {
      const dataThisYear = getDataForTimePeriod('this-year');
      const dataLastYear = getDataForTimePeriod('last-year');
      const diseasesCompared = { diseases: {} };

      // we need to first go trough all diseases from this year and find matching in last year,
      // but also the other way around because disease A may be in year 1 but not year 2 or vice versa

      for (const diseaseThisYear of dataThisYear.diseases) {
        const diseaseLastYear = dataLastYear.diseases.find(
          (d) => d.slaughterhouse.name === diseaseThisYear.slaughterhouse.name,
        );
        if (diseaseLastYear && !diseasesCompared[diseaseLastYear.name]) {
          diseasesCompared.diseases[diseaseLastYear.slaughterhouse.name] = {
            thisYear: diseaseThisYear,
            lastYear: diseaseLastYear,
          };
        }
      }

      for (const diseaseLastYear of dataLastYear.diseases) {
        const diseaseThisYear = dataThisYear.diseases.find(
          (d) => d.slaughterhouse.name === diseaseLastYear.slaughterhouse.name,
        );
        if (diseaseThisYear && !diseasesCompared[diseaseThisYear.name]) {
          diseasesCompared.diseases[diseaseThisYear.slaughterhouse.name] = {
            thisYear: diseaseThisYear,
            lastYear: diseaseLastYear,
          };
        }
      }

      diseasesCompared.partially_discarded = {
        lastYear: dataLastYear?.partially_discarded,
        thisYear: dataThisYear?.partially_discarded,
      };
      diseasesCompared.total_discarded = {
        lastYear: dataLastYear?.total_discarded,
        thisYear: dataThisYear?.total_discarded,
      };

      const getDiscardedDiffByDiscardedKindAndTarget = (kind, target) => {
        const percDiff = // comparing percentage difference is not possible if the initial value is 0 (initial-target)/initial is NaN
          !isThisLastYearValidForPercentageDiff(diseasesCompared[kind]?.thisYear?.[target]?.percentage, diseasesCompared[kind]?.lastYear?.[target]?.percentage) ? 'N/A'
            : (((diseasesCompared[kind]?.thisYear?.[target]?.percentage)
              - (diseasesCompared[kind]?.lastYear?.[target]?.percentage || 0))
              / diseasesCompared[kind]?.lastYear?.[target]?.percentage) * 100;
        const cntDiff = (diseasesCompared[kind]?.thisYear?.[target]?.count || 0)
          - (diseasesCompared[kind]?.lastYear?.[target]?.count || 0);

        return [
          {
            value: formatPercentage(percDiff),
            customDisplayValue: percentageDisplay(percDiff, { showIncrement: true, formatPercentage: true, showPercentageSign: true }),
          },
          {
            value: cntDiff,
            customDisplayValue: countDisplay(cntDiff),
          },
        ];
      };

      return {
        headers: [
          { value: 'Forskjell syke i år og i fjor', itemProps: { colSpan: 2 } },
          {
            value: 'Forskjell slakteri i år og i fjor',
            itemProps: { colSpan: 2 },
          },
        ],
        subHeaders: repeatNTimesIntoFlatArray(2, [
          { value: '%' },
          { value: 'Antall' },
        ]),
        data: Object.getOwnPropertyNames(diseasesCompared.diseases)
          .map((diseaseName) => {
            // comparing percentage difference is not possible if the initial value is a kind of zero(0, "0.00" etc) (initial-target)/initial is NaN
            const farmerPercDiffValue = !isThisLastYearValidForPercentageDiff(diseasesCompared.diseases[diseaseName].thisYear?.farmer?.percentage, diseasesCompared.diseases[diseaseName].lastYear?.farmer?.percentage)
              ? 'N/A'
              : (
                ((diseasesCompared.diseases[diseaseName].thisYear
                  ?.farmer?.percentage)
                  - (diseasesCompared.diseases[diseaseName].lastYear
                    ?.farmer?.percentage || 0))
                / (diseasesCompared.diseases[diseaseName].lastYear
                  ?.farmer?.percentage)) * 100;
            const farmerDiffCnt = (diseasesCompared.diseases[diseaseName].thisYear?.farmer
              ?.count || 0)
              - (diseasesCompared.diseases[diseaseName].lastYear?.farmer
                ?.count || 0);

            const slaughterhousePercDiffValue = !isThisLastYearValidForPercentageDiff(diseasesCompared.diseases[diseaseName].thisYear
              ?.slaughterhouse?.percentage, diseasesCompared.diseases[diseaseName].lastYear
                ?.slaughterhouse?.percentage)
              ? 'N/A'
              : ((diseasesCompared.diseases[diseaseName].thisYear
                ?.slaughterhouse?.percentage
                - diseasesCompared.diseases[diseaseName].lastYear
                  ?.slaughterhouse?.percentage || 0)
                / diseasesCompared.diseases[diseaseName].lastYear
                  ?.slaughterhouse?.percentage
              ) * 100;
            const slaughterhouseDiffCnt = diseasesCompared.diseases[diseaseName].thisYear.slaughterhouse
              .count
              - diseasesCompared.diseases[diseaseName].lastYear.slaughterhouse
                .count;

            return {
              diseaseData: [
                { value: diseaseName },
                {
                  value: formatPercentage(farmerPercDiffValue),
                  customDisplayValue: percentageDisplay(farmerPercDiffValue, { showIncrement: true, formatPercentage: true, showPercentageSign: true }),
                },
                {
                  value: farmerDiffCnt,
                  customDisplayValue: countDisplay(farmerDiffCnt),
                },
                {
                  value: formatPercentage(slaughterhousePercDiffValue),
                  customDisplayValue: percentageDisplay(slaughterhousePercDiffValue, { showIncrement: true, formatPercentage: true, showPercentageSign: true }),
                },
                {
                  value: slaughterhouseDiffCnt,
                  customDisplayValue: countDisplay(slaughterhouseDiffCnt),

                },
              ],
            };
          })
          .concat([
            {
              diseaseData: [
                { value: 'Helkassert' },
                ...getDiscardedDiffByDiscardedKindAndTarget(
                  'total_discarded',
                  'farmer',
                ),
                ...getDiscardedDiffByDiscardedKindAndTarget(
                  'total_discarded',
                  'slaughterhouse',
                ),
              ],
            },
          ])
          .concat([
            {
              diseaseData: [
                { value: 'Delkassert' },
                ...getDiscardedDiffByDiscardedKindAndTarget(
                  'partially_discarded',
                  'farmer',
                ),
                ...getDiscardedDiffByDiscardedKindAndTarget(
                  'partially_discarded',
                  'slaughterhouse',
                ),
              ],
            },
          ]),
      };
    }
    if (timePeriod === 'this-year') {
      return {
        headers: [
          { value: 'Syke hittil i år', itemProps: { colSpan: 2 } },
          { value: 'Slakteri hittil i år', itemProps: { colSpan: 2 } },
        ],
        subHeaders: repeatNTimesIntoFlatArray(2, [
          { value: '%' },
          { value: 'Antall' },
        ]),
        data: dataForCurrFilters.diseases
          .map((disease) => ({
            diseaseData: [
              { value: disease.slaughterhouse?.name },
              {
                value: formatPercentage(disease.farmer?.percentage),
                customDisplayValue: percentageDisplay(disease.farmer?.percentage),
              },
              { value: disease.farmer?.count || 0 },
              {
                value: formatPercentage(disease.slaughterhouse?.percentage),
                customDisplayValue: percentageDisplay(disease.slaughterhouse?.percentage),
              },
              { value: disease.slaughterhouse?.count },
            ],
          }))
          .concat(totalDiscardFarmerAndSlaughterhouse)
          .concat(partiallyDiscardFarmerAndSlaughterhouse),
      };
    }
    if (timePeriod === 'all-time') {
      return {
        headers: [
          { value: 'Syke for alle innmeldinger', itemProps: { colSpan: 2 } },
          { value: 'Slakteri alle innmeldinger', itemProps: { colSpan: 2 } },
        ],
        subHeaders: repeatNTimesIntoFlatArray(2, [
          { value: '%' },
          { value: 'Antall' },
        ]),
        data: dataForCurrFilters.diseases
          .map((disease) => ({
            diseaseData: [
              { value: disease.slaughterhouse?.name },
              {
                value: formatPercentage(disease.farmer?.percentage),
                customDisplayValue: percentageDisplay(disease.farmer?.percentage),
              },
              { value: disease.farmer?.count || 0 },
              {
                value: formatPercentage(disease.slaughterhouse?.percentage),
                customDisplayValue: percentageDisplay(disease.slaughterhouse?.percentage),
              },
              { value: disease.slaughterhouse?.count },
            ],
          }))
          .concat(totalDiscardFarmerAndSlaughterhouse)
          .concat(partiallyDiscardFarmerAndSlaughterhouse),
      };
    } else {
      console.error(`Missing handling of time frame for table: ${timePeriod}`);
      return { data: [] };
    }
  };

  const getComparePeriod = () => {
    const statisticsLatestGenerated = moment(
      slaughterhouseStatistics.data.last_generated,
    );
    return `1.1 - ${statisticsLatestGenerated.format('D.M')}`;
  };

  const tableInfo = missingDataOrSetupBase || missingDataOrSetupCurrentView()
    ? null
    : getTableInformationByTimePeriod(selectedTimePeriod.value);

  const dataForCurrFilters = selectedTimePeriod?.value === 'compare-this-last-year'
    || missingDataOrSetupBase
    || missingDataOrSetupCurrentView()
    ? null
    : getDataForTimePeriod(selectedTimePeriod.value);

  const dataForThisLastYearCompare = selectedTimePeriod?.value !== 'compare-this-last-year'
    || missingDataOrSetupBase
    || missingDataOrSetupCurrentView()
    ? null
    : {
      thisYear: getDataForTimePeriod('this-year'),
      lastYear: getDataForTimePeriod('last-year'),
    };

  const isCompareThisLastYear = selectedTimePeriod?.value === 'compare-this-last-year';

  const GeneralStats = ({ target }) => {
    const statsPostfix = target === 'slaughterhouse' ? '_pr_vendor' : '';
    return (
      <Row justify="center" wrap>
        {(selectedAnimalKind.value === 'pigs' && (
          <GeneralStatsEntity
            changeArrow={isCompareThisLastYear}
            data={Math.round(
              isCompareThisLastYear
                ? ((dataForThisLastYearCompare.thisYear.generalStats[target][
                  `meat_percent_avg${statsPostfix}`
                ]
                  - dataForThisLastYearCompare.lastYear.generalStats[target][
                  `meat_percent_avg${statsPostfix}`
                  ])
                  / dataForThisLastYearCompare.lastYear.generalStats[target][
                  `meat_percent_avg${statsPostfix}`
                  ])
                * 100
                : dataForCurrFilters.generalStats[target][
                `meat_percent_avg${statsPostfix}`
                ],
            )}
            unit="%"
            primaryDescription="Kjøttprosent"
            secondaryDescription={selectedTimePeriod.timeDescription}
          />
        )) || (
            <>
              {isCompareThisLastYear && (
                <>
                  <GeneralStatsEntity
                    data={
                      dataForThisLastYearCompare.thisYear.generalStats[target][
                      `classification_avg${statsPostfix}`
                      ]
                    }
                    primaryDescription="Snitt klasse i år"
                  />
                  <GeneralStatsEntity
                    data={
                      dataForThisLastYearCompare.lastYear.generalStats[target][
                      `classification_avg${statsPostfix}`
                      ]
                    }
                    primaryDescription="Snitt klasse i fjor"
                  />
                  <GeneralStatsEntity
                    data={
                      dataForThisLastYearCompare.thisYear.generalStats[target][
                      `fat_group_avg${statsPostfix}`
                      ]
                    }
                    primaryDescription="Snitt fettgruppe i år"
                  />
                  <GeneralStatsEntity
                    data={
                      dataForThisLastYearCompare.lastYear.generalStats[target][
                      `fat_group_avg${statsPostfix}`
                      ]
                    }
                    primaryDescription="Snitt fettgruppe i fjor"
                  />
                </>
              )}
              {!isCompareThisLastYear && (
                <>
                  <GeneralStatsEntity
                    data={
                      dataForCurrFilters.generalStats[target][
                      `classification_avg${statsPostfix}`
                      ]
                    }
                    primaryDescription="Snitt klasse"
                    secondaryDescription={selectedTimePeriod.timeDescription}
                  />
                  <GeneralStatsEntity
                    data={
                      dataForCurrFilters.generalStats[target][
                      `fat_group_avg${statsPostfix}`
                      ]
                    }
                    primaryDescription="Snitt fettgruppe"
                    secondaryDescription={selectedTimePeriod.timeDescription}
                  />
                </>
              )}
            </>
          )}
        <GeneralStatsEntity
          changeArrow={isCompareThisLastYear}
          data={formatAmount(
            Math.round(
              isCompareThisLastYear
                ? dataForThisLastYearCompare.thisYear.generalStats[target][
                `weight_kg_avg${statsPostfix}`
                ]
                - dataForThisLastYearCompare.lastYear.generalStats[target][
                `weight_kg_avg${statsPostfix}`
                ]
                : dataForCurrFilters.generalStats[target][
                `weight_kg_avg${statsPostfix}`
                ],
            ),
          )}
          unit="kg"
          primaryDescription="Snitt vekt"
          secondaryDescription={selectedTimePeriod.timeDescription}
        />
        <GeneralStatsEntity
          changeArrow={isCompareThisLastYear}
          data={formatAmount(
            Math.round(
              isCompareThisLastYear
                ? dataForThisLastYearCompare.thisYear.generalStats[target][
                `count${statsPostfix}`
                ]
                - dataForThisLastYearCompare.lastYear.generalStats[target][
                `count${statsPostfix}`
                ]
                : dataForCurrFilters.generalStats[target][
                `count${statsPostfix}`
                ],
            ),
          )}
          primaryDescription="Antall slakt"
          secondaryDescription={selectedTimePeriod.timeDescription}
        />

      </Row>
    );
  };

  const getChartData = (tableInfo) => {
    const config = {
      groupSelectEnabled: true,
      labelFrequencyOffsetPoints: true,
      groupSelectPosition: 'bottom',
      decimalSeparator: '.',
      groupSelectLabelScale: 0.8,
      labelFrequency: 2,
      marginRight: '20',
      barRadiusRatio: 0,
      flipAxes: true,
    };
    if (selectedTimePeriod.value === 'compare-this-last-year') {
      config.labelFrequency = 4;
    }
    if (selectedTimePeriod.value === 'last-settlement') {
      config.labelFrequency = 1;
    }
    const axes = [
      {
        label: 'Prosent',
        rounding: 1,
      },
    ];

    const DISEASE_NAME_IDX = 0;
    const PERC_FARMER_IDX = 1;
    const CNT_FARMER_IDX = 2;
    const PERC_SLAUGHTERHOUSE_IDX = 3;
    const CNT_SLAUGHTERHOUSE_IDX = 3;

    const farmerColorOne = getBrandingColor('statisticsGraphFarmerGroup');
    const farmerColorTwo = getBrandingColor('statisticsGraphFarmerGroupTwo');
    const slaughterhouseColorOne = getBrandingColor('statisticsGraphSlaughterhouseGroup');
    const slaughterhouseColorTwo = getBrandingColor('statisticsGraphSlaughterhouseGroupTwo');

    const diseaseNames = tableInfo.data.map((row) => row.diseaseData[0]);
    const commonGroup = {
      axis: 0,
      type: 'bar',
    };

    const groups = {
      'last-settlement': [
        {
          ...commonGroup,
          name: '% produsent siste leveranse',
          color: farmerColorOne,
        },
      ],
      'this-year': [
        {
          ...commonGroup,
          name: '% produsent hittil i år ',
          color: farmerColorOne,
        },
        {
          ...commonGroup,
          name: '% slakteri hittil i år',
          color: slaughterhouseColorOne,
        },
      ],
      'last-year': [
        {
          ...commonGroup,
          name: '% produsent i fjor',
          color: farmerColorOne,
        },
        {
          ...commonGroup,
          name: '% slakteri i fjor',
          color: slaughterhouseColorOne,
        },
      ],
      'compare-this-last-year': [
        {
          ...commonGroup,
          name: '% produsent hittil i år ',
          color: farmerColorOne,
        },
        {
          ...commonGroup,
          name: '% produsent i fjor',
          color: farmerColorTwo,
        },
        {
          ...commonGroup,
          name: '% slakteri hittil i år',
          color: slaughterhouseColorOne,
        },
        {
          ...commonGroup,
          name: '% slakteri i fjor',
          color: slaughterhouseColorTwo,
        },
      ],
      'all-time': [
        {
          ...commonGroup,
          name: '% produsent alle innmeldinger',
          color: farmerColorOne,
        },
        {
          ...commonGroup,
          name: '% slakteri alle innmeldinger',
          color: slaughterhouseColorOne,
        },
      ],
    }[selectedTimePeriod.value] || [];

    const labels = [];
    const values = [];

    if (selectedTimePeriod.value === 'compare-this-last-year') {
      const tableInfoThisYear = getTableInformationByTimePeriod('this-year');
      const tableInfoLastYear = getTableInformationByTimePeriod('last-year');

      const diseaseNamesBothYears = new Set(tableInfoThisYear.data.map((row) => row.diseaseData[DISEASE_NAME_IDX].value)
        .concat(tableInfoLastYear.data.map((row) => row.diseaseData[DISEASE_NAME_IDX].value)));

      Array.from(diseaseNamesBothYears).map((diseaseName, i, diseaseNameBothYearsArr) => {
        for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
          labels.push(diseaseNameBothYearsArr[i]);
        }

        const rowThisYear = tableInfoThisYear.data.find((row) => row.diseaseData[DISEASE_NAME_IDX].value === diseaseName);
        const rowLastYear = tableInfoLastYear.data.find((row) => row.diseaseData[DISEASE_NAME_IDX].value === diseaseName);
        values.push({ group: 0, label: (i * 4) + 0, value: rowThisYear ? parseFloat(rowThisYear.diseaseData[PERC_FARMER_IDX].value) : 0.0 });
        values.push({ group: 1, label: (i * 4) + 1, value: rowLastYear ? parseFloat(rowLastYear.diseaseData[PERC_FARMER_IDX].value) : 0.0 });
        values.push({ group: 2, label: (i * 4) + 2, value: rowThisYear ? parseFloat(rowThisYear.diseaseData[PERC_SLAUGHTERHOUSE_IDX].value) : 0.0 });
        values.push({ group: 3, label: (i * 4) + 3, value: rowLastYear ? parseFloat(rowLastYear.diseaseData[PERC_SLAUGHTERHOUSE_IDX].value) : 0.0 });
      });
    } else {
      for (let diseaseNameIdx = 0; diseaseNameIdx < diseaseNames.length; diseaseNameIdx++) {
        for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
          labels.push(diseaseNames[diseaseNameIdx].value);
        }
      }

      tableInfo.data.map((row, i) => {
        if (['all-time', 'this-year', 'last-year'].includes(selectedTimePeriod.value)) {
          values.push({ group: 0, label: i * 2, value: parseFloat(row.diseaseData[PERC_FARMER_IDX].value) });
          values.push({ group: 1, label: i * 2 + 1, value: parseFloat(row.diseaseData[PERC_SLAUGHTERHOUSE_IDX].value) });
        } else if (selectedTimePeriod.value === 'last-settlement') {
          values.push({ group: 0, label: i, value: parseFloat(row.diseaseData[PERC_FARMER_IDX].value) });
        }
      });
    }

    return {
      values,
      config,
      axes,
      labels,
      groups,
    };
  };

  const GeneralStatsForFarmerHeading = ({ forExpanded }) => (
    <Header>
      {forExpanded && `Mine ${kindToDisplay[selectedAnimalKind.value]}`}
      {!forExpanded && capitalize(kindToDisplay[selectedAnimalKind.value])}
      <span style={{ marginRight: '0.3em' }}>,</span>
      <span style={{ color: 'color: #6d6d6d' }}>
        {selectedAnimalSubkind.value === 'ALL_SUBKINDS'
          ? 'Alle typer'
          : selectedAnimalSubkind.value}
      </span>
    </Header>
  );
  const hasDiseaseInfoForCurrTimeFrame = tableInfo && tableInfo.data && tableInfo.data.length > 0;

  const displayLoading = missingDataOrSetupBase && !hasErrorLoadingMain && !hasErrorLoadingDataContent;
  const chartData = !hasErrorLoadingDataContent && !displayLoading && !hasErrorLoadingMain && hasDiseaseInfoForCurrTimeFrame && !missingDataOrSetupCurrentView() && getChartData(tableInfo);

  return (
    <StatisticsPage
      title="Statistikk"
      paddingBottom="9em"
    >
      <Breadcrumbs
        breadcrumbs={[{ title: 'Statistikk', url: '/statistics' }]}
        wrapperStyle={css`
            margin-bottom: 1.5rem;
            `}
      />
      <Label as="h1" size="2.5em" marginTop="0.5em" marginBottom="0.5em">
        Statistikk
      </Label>
      <Label color="#686a6e" marginBottom="2em">
        <Helptext name="statistics-info" />
      </Label>

      {(missingDataOrSetupBase && !hasErrorLoadingMain && !hasErrorLoadingDataContent && <LoadingIndicator withText />)
        || (missingSlaughters && <Label padding="3em">Du har ikke noe data å vise statistikk for enda...</Label>)
        || (!hasErrorLoadingMain ? (
          <>
            <FilterRow align="center" marginBottom="2em" wrap>
              {/* Time period */}
              <FilterDropdown
                label="Vis statistikk"
                items={getValidTimePeriods(subkindKeeping, allTimePeriods)}
                selected={selectedTimePeriod}
                onChange={(item) => {
                  setSeletedTimePeriod(item);
                  setDefaultSelectedFilters(
                    animalKindsWithFarmerStats.data,
                    ['KIND', 'SUBKIND'],
                    item,
                  );
                }}
                minWidthContent="12em"
              />

              {/* Kind */}
              <FilterDropdown
                label="for"
                items={getValidKindsForTimePeriod(selectedTimePeriod, subkindKeeping, kindToDisplay).map((e) => ({
                  text: capitalize(e.text),
                  value: e.value,
                }))}
                minWidthContent="6em"
                selected={{
                  value: selectedAnimalKind,
                  text: capitalize(kindToDisplay[selectedAnimalKind.value]),
                }}
                onChange={(item) => {
                  setSelectedAnimalKind(item);
                  setDefaultSelectedFilters(animalKindsWithFarmerStats.data, [
                    'SUBKIND',
                  ]);
                }}
              />

              {/* Subkind */}
              <FilterDropdown
                label="av type"
                items={getValidSubkindsForTimePeriodAndKind(selectedTimePeriod, selectedAnimalKind, subkindKeeping)}
                minWidthContent="10em"
                selected={
                  selectedAnimalSubkind.value === 'ALL_SUBKINDS'
                    ? { value: 'ALL_SUBKINDS', text: 'Alle typer' }
                    : selectedAnimalSubkind
                }
                onChange={(item) => {
                  setSelectedAnimalSubkind(item);
                  setDefaultSelectedFilters(
                    animalKindsWithFarmerStats.data,
                    [],
                  );
                }}
              />
            </FilterRow>

            {missingDataOrSetupCurrentView() && !hasErrorLoadingDataContent ? (
              <LoadingIndicator withText />
            ) : !hasErrorLoadingDataContent ? (
              <>
                {(selectedTimePeriod.value === 'last-year'
                  || selectedTimePeriod.value === 'compare-this-last-year') && (
                    <p
                      style={{
                        fontStyle: 'italic',
                        marginBottom: '-1em',
                        marginTop: '-1em',
                      }}
                    >
                      Periode i fjor:
                      {' '}
                      {getComparePeriod()}
                    </p>
                  )}
                <GeneralStatsForFarmerHeading />
                <GeneralStats target="farmer" />

                <ExpandGeneralStats
                  initial="collapsed"
                  expanded={
                    generalStatsExpanded
                    && selectedTimePeriod.value !== 'last-settlement'
                  }
                  variants={{
                    visible: {
                      height: 'auto',
                      oveflow: 'visible',
                      transition: { duration: 0.3 },
                    },
                    collapsed: {
                      height: 0,
                      overflow: 'hidden',
                    },
                  }}
                  animate={
                    generalStatsExpanded
                      && selectedTimePeriod !== null
                      && selectedTimePeriod.value !== 'last-settlement'
                      ? 'visible'
                      : 'collapsed'
                  }
                >
                  {selectedTimePeriod.value
                    && selectedTimePeriod.value !== 'last-settlement' && (
                      <>
                        <GeneralStatsForFarmerHeading
                          style={{ lineHeight: '0.9em' }}
                          forExpanded
                        />
                        <GeneralStats target="farmer" />
                        <Header style={{ lineHeight: '0.9em' }}>
                          Gjennomsnitt slakteri totalt
                        </Header>
                        <GeneralStats target="slaughterhouse" />
                        <Header style={{ lineHeight: '0.9em' }}>
                          Totalt for slakteri
                        </Header>
                        <GeneralStatsEntity
                          unit="kg"
                          primaryDescription="Total vekt"
                          changeArrow={isCompareThisLastYear}
                          secondaryDescription={
                            selectedTimePeriod.timeDescription
                          }
                          data={formatAmount(
                            Math.round(
                              (isCompareThisLastYear
                                && dataForThisLastYearCompare.thisYear.generalStats
                                  .slaughterhouse.weight_kg_tot
                                - dataForThisLastYearCompare.lastYear
                                  .generalStats.slaughterhouse
                                  .weight_kg_tot)
                              || dataForCurrFilters.generalStats.slaughterhouse
                                .weight_kg_tot,
                            ),
                          )}
                        />
                        <GeneralStatsEntity
                          changeArrow={isCompareThisLastYear}
                          primaryDescription="Antall slakt"
                          secondaryDescription={
                            selectedTimePeriod.timeDescription
                          }
                          data={formatAmount(
                            (isCompareThisLastYear
                              && dataForThisLastYearCompare.thisYear.generalStats
                                .slaughterhouse.count
                              - dataForThisLastYearCompare.lastYear.generalStats
                                .slaughterhouse.count)
                            || dataForCurrFilters.generalStats.slaughterhouse
                              .count,
                          )}
                        />
                      </>
                    )}

                </ExpandGeneralStats>
                {selectedTimePeriod.value !== 'last-settlement' && (
                  <ActionButton
                    ref={referenceWidthElem}
                    onClick={() => setGeneralStatsExpanded((old) => !old)}
                  >
                    {generalStatsExpanded ? 'Lukk' : 'Vis mer'}
                  </ActionButton>
                )}
                <Header style={{ marginTop: '1.5em', marginBottom: '0.68em' }}>
                  Sykdommer
                  {' '}
                  {selectedTimePeriod.timeDescription}
                  {selectedBottomView === 'graph'
                    && <Label size="0.8rem" marginTop="0.2rem" color="#525252">Trykk på eller beveg musepekeren over kolonnene for å se verdier.</Label>}
                </Header>

                <SwtichWrapper>
                  <Switch
                    options={[{ label: 'Graf', value: 'graph' }, { label: 'Tabell', value: 'table' }]}
                    value={selectedBottomView}
                    wrapperStle={{ maxWidth: '140px' }}
                    onChange={(val) => {
                      setSelectedBottomView(val);
                    }}
                    nameKey="label"
                    background="rgba(0, 0, 0, 0.2)"
                  />
                </SwtichWrapper>

                {!hasDiseaseInfoForCurrTimeFrame
                  && <p style={{ fontStyle: 'italic' }}>Fant ingen sykdommer for valgt filtrering</p>}

                {selectedBottomView === 'table' && hasDiseaseInfoForCurrTimeFrame
                  && (
                    <DiseaseStatisticsTable
                      wrapperStyle={{ maxWidth: '90vw' }}
                      IEWrapperCSS={css`
                      min-height: 320px;
                      margin-top: 1em;
                      padding-bottom: 12em;
                    `}
                      data={tableInfo.data}
                      headerElem={<DiseaseNameHeader />}
                      headers={tableInfo.headers}
                      subHeaders={tableInfo.subHeaders}
                      expandCollapseButtonEnabled
                      key={`${selectedTimePeriod.timeDescription}-${selectedAnimalKind?.value}${selectedAnimalSubkind?.value}`}
                    />
                  )}
                {selectedBottomView === 'graph' && hasDiseaseInfoForCurrTimeFrame && chartData
                  && (
                    <>
                      <div style={{
                        height: `${250 + (35 * chartData.values.length)}px`,
                        position: 'relative',
                      }}
                      >
                        <Chart
                          key={selectedTimePeriod.timeDescription}
                          data={chartData}
                          scale={pageSize?.width < 500 ? '0.8' : '1'}
                          /* A bug where you have to scroll for values to be displayed */
                          skipAnimation={chartData.values.length > 7}
                        />
                      </div>
                      <p style={{
                        fontStyle: 'italic', fontSize: '0.9em', margin: '0', marginTop: '1.6em', marginLeft: '4.1em',
                      }}
                      >
                        *Verdier er rundet av til nærmeste hundredel.
                      </p>
                    </>
                  )}

              </>
            ) : (
              <Label
                style={{ fontSize: '1.5em', fontStyle: 'itatlic' }}
              >
                Kunne ikke laste inn dataen...
              </Label>
            )}
          </>
        ) : <Label style={{ fontSize: '1.5em', fontStyle: 'itatlic' }}>Kunne ikke laste inn dataen...</Label>)}
    </StatisticsPage>
  );
};

export default Statistics;
